import { Disclosure } from '@headlessui/react'
import { NavLink, useLocation, useSearchParams } from 'react-router-dom'
import { Crown } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronToggle } from '../ChevronToggle'
import ChipV2 from './ChipV2'
import { NewList } from './NewList'
import { NewPipeline } from './NewPipeline'
import { Feature } from 'app/components/auth/Feature'
import { NavItem } from 'app/components/sidebar/NavItem'
import { SidebarChrome } from 'app/components/sidebar/SidebarChrome'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { useHomepage } from 'app/hooks/useHomepage'
import { useNavItems } from 'app/hooks/useNavItems'
import type { CustomPageJson } from 'app/types/entities'
import { notifySuccess } from 'app/services/notify.client'
import { hasRole, useUser } from 'app/utils'
import { titleCase } from 'app/utils/titleCase'
import { bg } from 'lib/background'
import { useCustomPageCreated } from 'lib/customPageEvents'
import { debug } from 'lib/logging'
import { hasNavProduct } from 'lib/sidebarItems'

export { hasNavProduct } from 'lib/sidebarItems'

export const SideBarV2 = observer(function SideBarV2(): ReactElement {
  const user = useUser()
  if (!user) return <></>

  const searchParams = useSearchParams()[0]
  // Product gates apply only inside a workspace; org-level users navigate above products.
  // Mirrors the legacy `hasNavProduct(workspace)` guard (TRK-5965).
  const inWorkspace = user.workspaceId != null
  const {
    navItems,
    permissionsLoading,
    products,
    staffV2Enabled,
    customPages,
    pipelines,
    refetchCustomPages,
    refetchPipelines,
    refreshSidebarCounts,
  } = useNavItems(user)
  const location = useLocation()

  // Owns the pin: which key it lives under, and the Action Center default that stands in
  // when the user has not pinned anything of their own.
  const homepage = useHomepage()

  // Set browser tab title from the active sidebar item
  const activePageName = useMemo(() => {
    for (const item of navItems) {
      // Top-level item with href that is active (e.g. Inbox)
      if (item.href != null && item.active) return item.name
      // Parent section with children
      if (item.children) {
        // Tab sections (Contacts, Activities, Tasks, Pipelines) →
        // use the parent name when active. Handles empty children
        // (e.g. Pipelines loading or workspace with no pipelines).
        if (item.canCreateNewList || item.createPipeline) {
          if (item.active) return item.name
          continue
        }
        // Sections with distinct child pages (Settings, Analytics, AI Agent) →
        // use the active child name, fall back to parent if no child is active.
        const activeChild = item.children.find((child) => child.active)
        if (activeChild) return activeChild.name
        if (item.active) return item.name
      }
    }
    return undefined
  }, [navItems])
  useDocumentTitle(activePageName)

  // State for managing open panels - Array to track which panels are open
  const [openPanels, setOpenPanels] = useState<string[]>([])
  const prevPathnameRef = useRef<string | null>(null)
  // Track panels user has manually collapsed (so we don't re-open them)
  const userCollapsedRef = useRef<Set<string>>(new Set())

  // Update open panels when pathname changes OR when new active items appear
  // This handles the case where Settings loads after initial permission check
  useEffect(() => {
    if (permissionsLoading || navItems.length === 0) return

    const pathnameChanged = prevPathnameRef.current !== location.pathname
    prevPathnameRef.current = location.pathname

    // Find all panels that should be open based on active state
    const activePanels: string[] = []
    navItems.forEach((item) => {
      if (item.href == null && item.name) {
        const hasActiveChild = item.children?.some((child) => child.active) || false
        if (item.active || hasActiveChild) {
          activePanels.push(item.name)
        }
      }
    })

    if (pathnameChanged) {
      // On navigation, reset to only active panels and clear user collapsed state
      userCollapsedRef.current.clear()
      setOpenPanels(activePanels)
    } else {
      // Check if there are active panels not currently open (and not manually collapsed)
      const missingActivePanels = activePanels.filter(
        (p) => !openPanels.includes(p) && !userCollapsedRef.current.has(p),
      )
      if (missingActivePanels.length > 0) {
        // If new active panels appeared (e.g., Settings loaded), add them
        setOpenPanels((prev) => [...new Set([...prev, ...missingActivePanels])])
      }
    }
  }, [permissionsLoading, navItems, location.pathname, openPanels])

  // Listen for custom page creation events to refresh the sidebar
  useCustomPageCreated(() => {
    debug(`sidebar`, `Custom page created, refreshing sidebar data`)
    bg(refetchCustomPages)
  })

  // Function to handle panel toggle.
  // Rules:
  // - Opening a new hierarchy closes the previously-open (non-active) one.
  // - We still keep the active hierarchy open.
  // - On navigation, we collapse to only the active hierarchy (see effect above).
  const handlePanelToggle = (panelKey: string) => {
    setOpenPanels((prevOpenPanels) => {
      if (prevOpenPanels.includes(panelKey)) {
        // User is collapsing - track it so we don't auto-reopen
        userCollapsedRef.current.add(panelKey)
        return prevOpenPanels.filter((key) => key !== panelKey)
      }

      // User is expanding - remove from collapsed tracking
      userCollapsedRef.current.delete(panelKey)

      const activePanelKeys = navItems
        .filter((item) => {
          if (item.href != null || !item.name) return false
          const hasActiveChild = item.children?.some((child) => child.active) || false
          return !!item.active || hasActiveChild
        })
        .map((item) => item.name!)
        .filter((key) => !userCollapsedRef.current.has(key)) // Respect user-collapsed panels

      // Keep active panels open + the newly opened panel.
      // This implicitly closes any previously-open non-active panels.
      return [...new Set([...activePanelKeys, panelKey])]
    })
  }

  return (
    <SidebarChrome>
      {navItems
        .filter((item) => item.if !== false && hasNavProduct(products, item.product, inWorkspace))
        .map((item, i) => (
          <Feature key={`parent-${item.name}-${i}`} name={item.feature}>
            {item.href == null ? (
              <Disclosure as="div" className="mb-2 flex flex-col">
                {() => {
                  const panelKey = item.name!
                  const isOpen = openPanels.includes(panelKey)
                  return (
                    <>
                      <Disclosure.Button
                        className="group flex h-9 items-center gap-x-2 pl-1.5 pr-[7px] hover:rounded-md hover:bg-(--fg-strong)/4"
                        onClick={() => handlePanelToggle(panelKey)}
                      >
                        {item.icon && (
                          <div className="flex h-9 w-9 items-center justify-center">
                            <item.icon className="aspect-square w-6 text-default" aria-hidden="true" />
                          </div>
                        )}
                        <div className="flex min-w-0 grow items-center gap-x-2">
                          <span className="min-w-0 truncate text-left text-heading-xs text-strong">
                            {titleCase(item.name)}
                          </span>
                          {item.superUserOnly && (
                            <div className="shrink-0 rounded-[4px] bg-amber-500/8 p-1">
                              <Crown width={14} height={14} className="text-amber-800" />
                            </div>
                          )}
                        </div>
                        <ChevronToggle
                          open={isOpen}
                          className="hidden aspect-square h-6 items-center justify-center rounded-[4px] bg-surface p-0 group-hover:flex"
                          chevronClassName="h-4 w-4 text-strong"
                        />
                      </Disclosure.Button>
                      {isOpen && (
                        <Disclosure.Panel static>
                          {item.children
                            ?.filter((child) => {
                              if (child.if === false) return false
                              if (!hasNavProduct(products, child.product, inWorkspace)) return false
                              if (child.superUserOnly && user.type !== `super`) return false
                              // When staff-v2 is enabled, permission filtering is handled by usePermissionFilteredSidebar
                              if (!staffV2Enabled && child.role && !hasRole(user, child.role)) return false
                              return true
                            })
                            .map((child, index) => {
                              // Handle divider items
                              if (child.divider) {
                                return (
                                  <div
                                    key={`${item.name}-divider-${index}`}
                                    className="my-2 ml-[46px] mr-2 border-t border-subtle"
                                  />
                                )
                              }
                              // Dynamically check if this child is a custom page for contacts, tasks, or activities
                              let customPage: CustomPageJson | undefined = undefined
                              if (item.name === `Activities` && child.href) {
                                customPage = customPages.activities.find(
                                  (page) => child.href === `/activities?tab=${page.id}`,
                                )
                              } else if (item.name === `Contacts` && child.href) {
                                customPage = customPages.contacts.find(
                                  (page) => child.href === `/contacts?tab=${page.id}`,
                                )
                              } else if (item.name === `Tasks` && child.href) {
                                customPage = customPages.tasks.find((page) => child.href === `/tasks?tab=${page.id}`)
                              }
                              return (
                                child.if !== false && (
                                  <Feature key={`${item.name}-${child.name}-${index}`} name={child.feature}>
                                    <NavItem
                                      customPage={customPage}
                                      item={{
                                        ...child,
                                        href: child.href || `#`,
                                        active: child.active || false,
                                      }}
                                      isDefault={homepage.href === child.href}
                                      lockedDefault={homepage.isDefaultHomepage && homepage.href === child.href}
                                      pipeline={pipelines.find((p) => child.href === `/pipelines?tab=${p.id}`)}
                                      onEditList={async () => {
                                        await refetchCustomPages()
                                        if (
                                          customPage &&
                                          searchParams.get(`tab`) === (customPage as CustomPageJson).id
                                        ) {
                                          window.location.reload()
                                        }
                                      }}
                                      onSetAsDefault={homepage.setHomepage}
                                      onRemovePipeline={refetchPipelines}
                                    />
                                  </Feature>
                                )
                              )
                            })}
                          {item.canCreateNewList && (
                            <Feature key={`${item.name}-new-list`} name={item.feature}>
                              <NewList
                                page={
                                  item.name === `Contacts` ? `contacts` : item.name === `Tasks` ? `tasks` : `activities`
                                }
                                onPageCreated={async () => {
                                  await refetchCustomPages()
                                  refreshSidebarCounts()
                                }}
                              />
                            </Feature>
                          )}
                          {item.createPipeline && (
                            <NewPipeline
                              key={`${item.name}-new-pipeline`}
                              onPipelineCreated={() => {
                                refetchPipelines()
                                refreshSidebarCounts()
                              }}
                            />
                          )}
                        </Disclosure.Panel>
                      )}
                    </>
                  )
                }}
              </Disclosure>
            ) : (
              <NavLink
                to={item.href ?? `#`}
                className={`group/nav-item relative mb-2 flex h-9 items-center gap-x-2 pl-1.5 pr-2 ${
                  item.active ? `rounded-md bg-(--fg-strong)/6` : `hover:rounded-md hover:bg-(--fg-strong)/4`
                }`}
                key={`${item.name}-${i}`}
              >
                {item.icon && (
                  <div className="flex h-9 w-9 items-center justify-center">
                    <item.icon className="aspect-square w-6 text-default" aria-hidden="true" />
                  </div>
                )}
                <div className="flex min-w-0 grow items-center gap-x-2">
                  <span className="flex h-9 min-w-0 items-center truncate text-left text-heading-xs text-strong">
                    {titleCase(item.name)}
                  </span>
                  {item.superUserOnly && (
                    <div className="shrink-0 rounded-[4px] bg-amber-500/8 p-1">
                      <Crown width={14} height={14} className="text-amber-800" />
                    </div>
                  )}
                </div>
                {/* Every top-level row that reaches this branch is a leaf (the
                    grouped ones take the Disclosure path above), so the pin belongs
                    on all of them, not just Inbox. Action Center and Workflows carry
                    no count, and `hide` keeps the empty badge out of their way. */}
                <ChipV2
                  total={item.count ?? 0}
                  hide={!item.count}
                  countColor="bg-primary-600"
                  href={item.href}
                  isDefault={homepage.href === item.href}
                  lockedDefault={homepage.isDefaultHomepage && homepage.href === item.href}
                  isActive={item.active}
                  onSetAsDefault={async () => {
                    const isCurrentlyPinned = homepage.explicitHref === item.href
                    await homepage.setHomepage(isCurrentlyPinned ? undefined : item.href)
                    notifySuccess(isCurrentlyPinned ? `Homepage reset` : `Homepage set to ${titleCase(item.name)}`)
                  }}
                />
              </NavLink>
            )}
          </Feature>
        ))}
    </SidebarChrome>
  )
})
